import {useMemo} from "react";
import {dataGridSortMoney, dataGridSortNumber, dataGridSortText} from "../../../utils/dataGridSorting.js";
import {useTranslation} from "react-i18next";
import {
    useRenderCurrencyValue,
    useRenderNumber,
    useRenderPercentValue
} from "../../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../../utils/useUserSettings.js";

export const GridType = {
    MarketingOrders: 'MarketingOrders',
    MarketingProfitability: 'MarketingProfitability',
    MarketingLeads: 'MarketingLeads',
}

export function useMarketingColumns(gridType) {

    let {t} = useTranslation();

    let renderCurrencyValue = useRenderCurrencyValue();
    let renderPercentValue = useRenderPercentValue();
    let renderNumber = useRenderNumber();

    let [userSettings] = useUserSettings();

    return useMemo(
        () => {

            let columns = [];

            columns.push({
                name: 'name',
                header: t('dashboard.source'),
                defaultFlex: 3,
                minWidth: 150,
                aggregate: false,
                sort: dataGridSortText,
            });

            columns.push({
                name: 'spendMarketing',
                header: t('dashboard.grid.spend-marketing'),
                desc: t('dashboard.grid.spend-marketing-long'),
                defaultFlex: 1.25,
                textAlign: 'end',
                minWidth: 100,
                render: (input) => renderCurrencyValue(input.data.spendMarketing, true),
                sort: dataGridSortMoney,
            });

            columns.push({
                name: userSettings?.isMarketing ? 'spendGoodsGross' : 'spendGoodsNet',
                header: t('dashboard.grid.spend-goods'),
                desc: t('dashboard.grid.spend-goods-long'),
                defaultFlex: 1.25,
                textAlign: 'end',
                minWidth: 100,
                show: gridType === GridType.MarketingProfitability,
                render: (input) => renderCurrencyValue(
                    userSettings?.isMarketing ? input.data.spendGoodsGross : input.data.spendGoodsNet,
                    true,
                ),
                sort: dataGridSortMoney,
            });

            if (gridType !== GridType.MarketingLeads) {
                columns.push({
                    name: userSettings?.isMarketing ? 'turnoverGross' : 'turnoverNet',
                    header: t('dashboard.turnover'),
                    defaultFlex: 1.25,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => renderCurrencyValue(
                        userSettings?.isMarketing ? input.data.turnoverGross : input.data.turnoverNet,
                        true
                    ),
                    sort: dataGridSortMoney,
                });

                columns.push({
                    name: userSettings?.isMarketing ? 'pnoGross' : 'pnoNet',
                    header: t('dashboard.grid.pno'),
                    desc: t('dashboard.grid.pno-long'),
                    defaultFlex: 0.75,
                    aggregate: false,
                    textAlign: 'end',
                    minWidth: 80,
                    render: (input) => renderPercentValue(
                        userSettings?.isMarketing ? input.data.pnoGross : input.data.pnoNet,
                    ),
                    sort: dataGridSortNumber,
                });

                columns.push({
                    name: userSettings?.isMarketing ? 'roasGross' : 'roasNet',
                    header: t('dashboard.grid.roas'),
                    desc: t('dashboard.grid.roas-long'),
                    aggregate: false,
                    textAlign: 'end',
                    minWidth: 80,
                    render: (input) => renderPercentValue(
                        userSettings?.isMarketing ? input.data.roasGross : input.data.roasNet,
                    ),
                    sort: dataGridSortNumber,
                });

                columns.push({
                    name: userSettings?.isMarketing ? 'poasGross' : 'poasNet',
                    header: t('dashboard.grid.poas'),
                    desc: t('dashboard.grid.poas-long'),
                    aggregate: false,
                    textAlign: 'end',
                    minWidth: 80,
                    render: (input) => renderPercentValue(
                        userSettings?.isMarketing ? input.data.poasGross : input.data.poasNet,
                    ),
                    sort: dataGridSortNumber,
                });

                if (gridType === GridType.MarketingProfitability) {
                    columns.push({
                        name: userSettings?.isMarketing ? 'profitGross' : 'profitNet',
                        header: t('dashboard.grid.profit'),
                        defaultFlex: 2,
                        textAlign: 'end',
                        minWidth: 80,
                        render: (input) => renderCurrencyValue(
                            userSettings?.isMarketing ? input.data.profitGross : input.data.profitNet,
                            true
                        ),
                        sort: dataGridSortMoney,
                    });

                    columns.push({
                        name: userSettings?.isMarketing ? 'profitPercentGross' : 'profitPercentNet',
                        header: t('dashboard.grid.profit-percent'),
                        defaultFlex: 1.25,
                        textAlign: 'end',
                        minWidth: 80,
                        show: gridType === GridType.MarketingProfitability,
                        render: (input) => renderPercentValue(
                            userSettings?.isMarketing
                                ? input.data.profitPercentGross
                                : input.data.profitPercentNet,
                            true
                        ),
                        sort: dataGridSortNumber,
                    });
               }

                columns.push({
                    name: userSettings?.isMarketing ? 'ordersGross' : 'ordersNet',
                    header: t('dashboard.grid.orders'),
                    desc: t('dashboard.grid.orders-long'),
                    defaultFlex: 0.6,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => renderNumber(
                        userSettings?.isMarketing ? input.data.ordersGross : input.data.ordersNet,
                    ),
                    sort: dataGridSortNumber,
                });

            }

            if (gridType === GridType.MarketingLeads) {
                columns.push({
                    name: 'totalLeads',
                    header: t('dashboard.leads'),
                    defaultFlex: 0.6,
                    textAlign: 'end',
                    minWidth: 80,
                    render: (input) => renderNumber(input.data.totalLeads),
                    sort: dataGridSortNumber,
                });

                columns.push({
                    name: 'verifiedLeads',
                    header: t('dashboard.verified-leads'),
                    defaultFlex: 0.6,
                    textAlign: 'end',
                    minWidth: 80,
                    render: (input) => renderNumber(input.data.verifiedLeads),
                    sort: dataGridSortNumber,
                });

                columns.push({
                    name: 'cpa',
                    header: t('dashboard.grid.cpa'),
                    desc: t('dashboard.grid.cpa-long'),
                    defaultFlex: 1,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => renderCurrencyValue(input.data.cpa, true),
                    sort: dataGridSortMoney,
                });
            }

            columns.push({
                name: 'clicks',
                header: t('dashboard.grid.clicks'),
                desc: t('dashboard.grid.clicks-long'),
                defaultFlex: 0.6,
                textAlign: 'end',
                minWidth: 80,
                render: (input) => renderNumber(input.data.clicks),
                sort: dataGridSortNumber,
            });

            columns.push({
                name: 'impressions',
                header: t('dashboard.grid.impressions'),
                desc: t('dashboard.grid.impressions-long'),
                defaultFlex: 1,
                textAlign: 'end',
                minWidth: 90,
                render: (input) => renderNumber(input.data.impressions),
                sort: dataGridSortNumber,
            });

            if (gridType !== GridType.MarketingProfitability) {
                columns.push({
                    name: 'cpc',
                    header: t('dashboard.grid.average-cpc'),
                    desc: t('dashboard.grid.average-cpc-long'),
                    defaultFlex: 0.8,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => renderCurrencyValue(input.data.cpc),
                    sort: dataGridSortMoney,
                });

                columns.push({
                    name: 'cpm',
                    header: t('dashboard.grid.cpm'),
                    desc: t('dashboard.grid.cpm-long'),
                    defaultFlex: 0.8,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => renderCurrencyValue(input.data.cpm, true),
                    sort: dataGridSortMoney,
                });

                columns.push({
                    name: 'ctr',
                    header: t('dashboard.grid.ctr'),
                    desc: t('dashboard.grid.ctr-long'),
                    defaultFlex: 0.8,
                    textAlign: 'end',
                    minWidth: 100,
                    render: (input) => renderPercentValue(input.data.ctr),
                    sort: dataGridSortNumber,
                });
            }

            columns.push({
                name: userSettings?.isMarketing ? 'conversionRateGross' : 'conversionRateNet',
                header: t('dashboard.grid.conversion-rate'),
                desc: t('dashboard.grid.conversion-rate-long'),
                defaultFlex: 0.8,
                textAlign: 'end',
                minWidth: 110,
                render: (input) => renderPercentValue(
                    userSettings?.isMarketing ? input.data.conversionRateGross : input.data.conversionRateNet
                ),
                sort: dataGridSortNumber,
            });

            return columns;
        },
        [gridType, userSettings],
    );
}
